import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import {
  ArrowTrendingUpIcon,
  BanknotesIcon,
  ChevronDownIcon,
  CubeIcon,
  CurrencyPoundIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

import HeadlineStat from "../../components/shared/HeadlineStat";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import ReportLayout, { ReportFilterSelect } from "./ReportLayout";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import {
  MockPartSale,
  MockVehicle,
  VEHICLES,
  buildPartSales,
  monthsAgo,
  today,
} from "./reportsMockData";

interface VehicleRollup {
  vehicle: MockVehicle;
  sales: MockPartSale[];
  revenue: number;
  profit: number;
  partCount: number;
  topCategory: string | null;
  lastSaleDate: string | null;
}

const fmtInt = (n: number) => n.toLocaleString("en-GB");

/**
 * Vehicle report — one row per donor vehicle the yard has handled, with the
 * sales generated from its components. Tap a row to expand and see each part
 * sale broken out.
 *
 * Backed by `reportsMockData` until the `/report/vehicle` endpoint ships.
 */
export default function VehicleReport() {
  const { setInfo } = usePageNameContext();
  const [dateRange, setDateRange] = useState({
    from: monthsAgo(12),
    to: today(),
  });
  const [makeFilter, setMakeFilter] = useState<string>("all");
  const [expandedVehicleId, setExpandedVehicleId] = useState<string | null>(
    null
  );

  useEffect(() => {
    setInfo({
      name: "Vehicle Report",
      desc: "Component sales attributed back to each donor vehicle",
    });
  }, [setInfo]);

  const allSales = useMemo(
    () => buildPartSales(`${dateRange.from}T00:00:00Z`, `${dateRange.to}T23:59:59Z`),
    [dateRange.from, dateRange.to]
  );

  const rollups: VehicleRollup[] = useMemo(() => {
    const byId = new Map<string, VehicleRollup>();
    for (const v of VEHICLES()) {
      byId.set(v.id, {
        vehicle: v,
        sales: [],
        revenue: 0,
        profit: 0,
        partCount: 0,
        topCategory: null,
        lastSaleDate: null,
      });
    }

    for (const s of allSales) {
      const row = byId.get(s.vehicleId);
      if (!row) continue;
      row.sales.push(s);
      row.revenue += s.total;
      row.profit += s.profit;
      row.partCount += s.quantity;
      if (!row.lastSaleDate || s.saleDate > row.lastSaleDate) {
        row.lastSaleDate = s.saleDate;
      }
    }

    // Derive the top-selling category per vehicle so we have a "headline
    // part type" column the yard manager can eyeball at a glance.
    for (const row of byId.values()) {
      if (!row.sales.length) continue;
      const tally = new Map<string, number>();
      for (const s of row.sales) {
        tally.set(s.partCategory, (tally.get(s.partCategory) ?? 0) + s.total);
      }
      row.topCategory = [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0];
    }

    return [...byId.values()]
      .filter((r) => (makeFilter === "all" ? true : r.vehicle.make === makeFilter))
      .sort((a, b) => b.revenue - a.revenue);
  }, [allSales, makeFilter]);

  const totals = useMemo(
    () => ({
      vehicles: rollups.length,
      vehiclesWithSales: rollups.filter((r) => r.sales.length > 0).length,
      revenue: rollups.reduce((s, r) => s + r.revenue, 0),
      profit: rollups.reduce((s, r) => s + r.profit, 0),
      parts: rollups.reduce((s, r) => s + r.partCount, 0),
      donorSpend: rollups.reduce((s, r) => s + r.vehicle.purchasePrice, 0),
    }),
    [rollups]
  );

  const avgRoi =
    totals.donorSpend === 0
      ? 0
      : Math.round((totals.revenue / totals.donorSpend) * 100);

  const makes = useMemo(() => {
    const set = new Set<string>();
    for (const v of VEHICLES()) set.add(v.make);
    return [...set].sort();
  }, []);

  const csvRows = useMemo(
    () =>
      rollups.map((r) => ({
        stockNumber: r.vehicle.stockNumber,
        registration: r.vehicle.registration,
        make: r.vehicle.make,
        model: r.vehicle.model,
        derivative: r.vehicle.derivative,
        year: r.vehicle.year,
        mileage: r.vehicle.mileage,
        fuelType: r.vehicle.fuelType,
        collectedOn: r.vehicle.collectedOn,
        partsSold: r.partCount,
        revenueGBP: r.revenue,
        profitGBP: r.profit,
        donorSpendGBP: r.vehicle.purchasePrice,
        roiPercent:
          r.vehicle.purchasePrice > 0
            ? Math.round((r.revenue / r.vehicle.purchasePrice) * 100)
            : 0,
        topCategory: r.topCategory,
        lastSaleDate: r.lastSaleDate ? r.lastSaleDate.slice(0, 10) : "",
      })),
    [rollups]
  );

  return (
    <ReportLayout
      title="Vehicle Report"
      description="Donor vehicles and the component sales attributed to each one. Click a row to expand the part-level breakdown."
      icon={TruckIcon}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      controls={
        <ReportFilterSelect
          label="Make"
          value={makeFilter}
          onChange={setMakeFilter}
          options={[
            { value: "all", label: "All makes" },
            ...makes.map((m) => ({ value: m, label: m })),
          ]}
          width="11rem"
        />
      }
      csvRows={csvRows}
      csvFilename="vehicle-report"
      headlineStats={
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <HeadlineStat
            tone="blue"
            icon={TruckIcon}
            value={fmtInt(totals.vehicles)}
            label="Vehicles"
            sublabel={`${fmtInt(totals.vehiclesWithSales)} with sales`}
          />
          <HeadlineStat
            tone="emerald"
            icon={CurrencyPoundIcon}
            value={PriceFormat(totals.revenue)}
            label="Total revenue"
            sublabel="Component sales"
          />
          <HeadlineStat
            tone="violet"
            icon={BanknotesIcon}
            value={PriceFormat(totals.profit)}
            label="Total profit"
            sublabel="After unit cost"
          />
          <HeadlineStat
            tone="cyan"
            icon={CubeIcon}
            value={fmtInt(totals.parts)}
            label="Parts sold"
            sublabel="Across all vehicles"
          />
          <HeadlineStat
            tone="amber"
            icon={CurrencyPoundIcon}
            value={PriceFormat(totals.donorSpend)}
            label="Donor spend"
            sublabel="Purchase price"
          />
          <HeadlineStat
            tone="rose"
            icon={ArrowTrendingUpIcon}
            value={`${fmtInt(avgRoi)}%`}
            label="Portfolio ROI"
            sublabel="Revenue / donor spend"
          />
        </div>
      }
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th className="w-6"></Th>
                <Th>Vehicle</Th>
                <Th>Collected</Th>
                <Th className="text-right">Parts sold</Th>
                <Th className="text-right">Revenue</Th>
                <Th className="text-right">Profit</Th>
                <Th className="text-right">ROI</Th>
                <Th>Top category</Th>
                <Th>Last sale</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rollups.map((r) => {
                const isOpen = expandedVehicleId === r.vehicle.id;
                const roi =
                  r.vehicle.purchasePrice === 0
                    ? 0
                    : Math.round((r.revenue / r.vehicle.purchasePrice) * 100);
                return (
                  <FragmentRow key={r.vehicle.id}>
                    <tr
                      className={classNames(
                        "cursor-pointer transition-colors hover:bg-primary-50/40",
                        isOpen ? "bg-primary-50/60" : ""
                      )}
                      onClick={() =>
                        setExpandedVehicleId(isOpen ? null : r.vehicle.id)
                      }
                    >
                      <td className="pl-4 pr-1 py-2 align-middle">
                        <ChevronDownIcon
                          className={classNames(
                            "h-4 w-4 text-slate-400 transition-transform",
                            isOpen ? "" : "-rotate-90"
                          )}
                          aria-hidden
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-900">
                            {r.vehicle.year} {r.vehicle.make} {r.vehicle.model}
                          </span>
                          <span className="text-[11px] text-slate-500">
                            {r.vehicle.derivative}
                          </span>
                          <span className="mt-1 font-mono text-[11px] text-slate-500">
                            {r.vehicle.registration} · {r.vehicle.stockNumber}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2 align-middle text-slate-600">
                        {formatDateTime(
                          r.vehicle.collectedOn,
                          DateTime.DATE_MED
                        )}
                      </td>
                      <td className="px-3 py-2 align-middle text-right font-semibold tabular-nums text-slate-900">
                        {fmtInt(r.partCount)}
                      </td>
                      <td className="px-3 py-2 align-middle text-right font-semibold tabular-nums text-slate-900">
                        {PriceFormat(r.revenue)}
                      </td>
                      <td
                        className={classNames(
                          "px-3 py-2 align-middle text-right font-semibold tabular-nums",
                          r.profit >= 0 ? "text-emerald-700" : "text-rose-600"
                        )}
                      >
                        {PriceFormat(r.profit)}
                      </td>
                      <td
                        className={classNames(
                          "px-3 py-2 align-middle text-right font-semibold tabular-nums",
                          roi >= 100 ? "text-emerald-700" : "text-slate-700"
                        )}
                      >
                        {fmtInt(roi)}%
                      </td>
                      <td className="px-3 py-2 align-middle text-slate-700">
                        {r.topCategory ?? "—"}
                      </td>
                      <td className="px-3 py-2 align-middle text-slate-600">
                        {r.lastSaleDate
                          ? formatDateTime(
                              r.lastSaleDate,
                              DateTime.DATE_MED
                            )
                          : "—"}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-slate-50/60">
                        <td colSpan={9} className="px-4 pb-4 pt-2">
                          <ExpandedSales sales={r.sales} />
                        </td>
                      </tr>
                    )}
                  </FragmentRow>
                );
              })}

              {rollups.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    No vehicles match the current filter.
                  </td>
                </tr>
              )}
            </tbody>

            {rollups.length > 0 && (
              <tfoot className="bg-slate-50">
                <tr className="text-sm font-semibold text-slate-700">
                  <td />
                  <td className="px-3 py-2">Totals</td>
                  <td />
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtInt(totals.parts)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {PriceFormat(totals.revenue)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {PriceFormat(totals.profit)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtInt(avgRoi)}%
                  </td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </ReportLayout>
  );
}

function Th({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={classNames(
        "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500",
        className ?? ""
      )}
    >
      {children}
    </th>
  );
}

/** React fragments can't be the child of a `<tbody>` sibling pattern with a
 * unique key, so we wrap two `<tr>`s in a helper fragment component.*/
function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function ExpandedSales({ sales }: { sales: MockPartSale[] }) {
  if (!sales.length) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-4 text-center text-xs text-slate-500">
        No part sales recorded for this vehicle in the selected range.
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-slate-100 text-xs">
        <thead className="bg-slate-50">
          <tr>
            <Th className="!text-[10px]">Sale date</Th>
            <Th className="!text-[10px]">Order</Th>
            <Th className="!text-[10px]">Part</Th>
            <Th className="!text-[10px]">Category</Th>
            <Th className="!text-[10px]">Channel</Th>
            <Th className="!text-[10px] !text-right">Qty</Th>
            <Th className="!text-[10px] !text-right">Unit</Th>
            <Th className="!text-[10px] !text-right">Total</Th>
            <Th className="!text-[10px] !text-right">Profit</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sales.slice(0, 30).map((s) => (
            <tr key={s.id} className="hover:bg-slate-50/60">
              <td className="whitespace-nowrap px-3 py-1.5 text-slate-600">
                {formatDateTime(s.saleDate, DateTime.DATE_MED)}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[11px] text-slate-500">
                {s.orderNumber}
              </td>
              <td className="px-3 py-1.5 text-slate-700">{s.partName}</td>
              <td className="px-3 py-1.5 text-slate-500">{s.partCategory}</td>
              <td className="px-3 py-1.5 text-slate-500">{s.salesChannel}</td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-700">
                {s.quantity}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-slate-700">
                {PriceFormat(s.unitPrice)}
              </td>
              <td className="whitespace-nowrap px-3 py-1.5 text-right font-semibold tabular-nums text-slate-900">
                {PriceFormat(s.total)}
              </td>
              <td
                className={classNames(
                  "whitespace-nowrap px-3 py-1.5 text-right font-semibold tabular-nums",
                  s.profit >= 0 ? "text-emerald-700" : "text-rose-600"
                )}
              >
                {PriceFormat(s.profit)}
              </td>
            </tr>
          ))}
        </tbody>
        {sales.length > 30 && (
          <tfoot>
            <tr>
              <td
                colSpan={9}
                className="bg-slate-50 px-3 py-1.5 text-[11px] italic text-slate-500"
              >
                Showing 30 of {sales.length} part sales. Use the CSV export to
                see the full list.
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
